/**
 * useNotifications — manages push notification permission and VAPID subscription.
 *
 * Usage:
 *   const { permissionState, isSupported, isSubscribed, requestPermission } = useNotifications()
 *
 * VAPID public key must be set in VITE_VAPID_PUBLIC_KEY env variable.
 * If the key is absent, subscription is skipped (permission prompt still works).
 */
import { useState, useEffect, useCallback } from 'react'
import { notificationsApi } from '@/lib/api'

export type NotifPermission = 'default' | 'granted' | 'denied' | 'unsupported'

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(b64)
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
}

function readPermission(): NotifPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return Notification.permission as NotifPermission
}

export function useNotifications() {
  const isSupported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window

  const [permissionState, setPermissionState] = useState<NotifPermission>(readPermission)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [swReg, setSwReg] = useState<ServiceWorkerRegistration | null>(null)

  // Register service worker on mount
  useEffect(() => {
    if (!isSupported) return

    navigator.serviceWorker
      .register('/sw.js')
      .then(async (reg) => {
        setSwReg(reg)
        const existing = await reg.pushManager.getSubscription()
        setIsSubscribed(!!existing)
      })
      .catch((err) => console.error('[SW] Registration failed:', err))
  }, [isSupported])

  const requestPermission = useCallback(async (): Promise<NotifPermission> => {
    if (!isSupported) return 'unsupported'

    const result = await Notification.requestPermission()
    const state = result as NotifPermission
    setPermissionState(state)

    if (state === 'granted' && swReg && VAPID_PUBLIC_KEY) {
      try {
        const subscription = await swReg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as ArrayBuffer,
        })
        const json = subscription.toJSON() as {
          endpoint: string
          keys: { p256dh: string; auth: string }
        }
        await notificationsApi.subscribe({
          endpoint: json.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
        })
        setIsSubscribed(true)
      } catch (err) {
        console.error('[Push] Subscription failed:', err)
      }
    }

    return state
  }, [isSupported, swReg])

  return {
    permissionState,
    isSupported,
    isSubscribed,
    requestPermission,
  }
}
